import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { Helmet } from "react-helmet-async";
import { toPng } from "html-to-image";
import { Download, Copy, Check, Loader2, Instagram, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { eur } from "@/lib/format";
import Seo from "@/components/Seo";

const LOGO = "/images/ee-logo.png";
const SITE = "shop.equineevolve.com";

const VARIANTS = [
  { id: 0, name: "Spotlight", format: "Story" },
  { id: 1, name: "Editorial", format: "Feed" },
  { id: 2, name: "Gold Frame", format: "Story" },
  { id: 3, name: "Legacy Quote", format: "Story" },
  { id: 4, name: "Price Feature", format: "Feed" },
];

const startDate = dayjs().add(2, "day");
const scheduleFor = (globalIndex, format) => {
  const d = startDate.add(globalIndex * 2, "day");
  const t = format === "Story" ? d.hour(19).minute(0) : d.hour(11).minute(30);
  return t.format("ddd D MMM · HH:mm");
};

const hashtags = (p) =>
  p.category === "jewellery"
    ? "#EquineEvolve #LuxuryEquestrian #GoldBangle #BespokeJewellery #EquestrianStyle #Handmade"
    : "#EquineEvolve #EquestrianLifestyle #LuxuryHome #Handmade #EquestrianStyle";

function genCaption(p, vId) {
  const price = eur(p.price);
  const bespoke = p.category === "jewellery";
  const insp = bespoke ? (p.inspired_by || "").replace(/^Inspired by /i, "").trim() : "";
  const lines = {
    0: `✨ ${p.name} ✨\n${bespoke ? "Bespoke gold, made to your measure" : "Luxury for the equestrian life"}${insp ? ` — a tribute to ${insp}` : ""}.\nFrom ${price}. Shop the collection — link in bio. 🐎`,
    1: `Crafted, not manufactured.\n${p.name} — ${p.short_description}\nFrom ${price}. Handcrafted to order.`,
    2: `Timeless. ${bespoke ? "Solid gold." : "Beautifully made."} Yours.\n${p.name} · ${price}\nReserve yours today — link in bio.`,
    3: `Wear a legend. 🏆\n${insp ? `Inspired by ${insp}. ` : ""}${p.name} — ${price}.\nMade to order.`,
    4: `From ${price}.\n${p.name} — secure yours before the next collection drops.\nLink in bio to shop. ✨`,
  };
  return `${lines[vId]}\n\n${hashtags(p)}`;
}

/* ---------- The downloadable card (fixed 360x640 = 9:16) ---------- */
function AssetCard({ p, vId, cardId }) {
  const img = p.images?.[0];
  const insp = (p.inspired_by || "").replace(/^Inspired by /i, "").trim();
  const base = "relative w-[360px] h-[640px] overflow-hidden bg-[#0C0A09] shrink-0";

  if (vId === 0) {
    return (
      <div id={cardId} className={base} data-testid={cardId}>
        <img src={img} alt={p.name} crossOrigin="anonymous" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.05) 38%, rgba(12,10,9,0.94))" }} />
        <div className="absolute inset-0 flex flex-col p-8">
          <div className="flex justify-center">
            <img src={LOGO} alt="Equine Evolve" crossOrigin="anonymous" className="h-11 object-contain" />
          </div>
          <div className="mt-auto">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">{p.category_name} · {p.category === "jewellery" ? "Bespoke" : "Signature"}</p>
            <h3 className="font-display text-[2.7rem] leading-[1.02] text-white mt-2">{p.name}</h3>
            <div className="h-px w-16 bg-[#D4AF37] my-4" />
            <p className="font-display text-3xl text-[#D4AF37]">From {eur(p.price)}</p>
            <p className="text-[10px] tracking-[0.25em] uppercase text-white/55 mt-4">{SITE}</p>
          </div>
        </div>
      </div>
    );
  }

  if (vId === 1) {
    return (
      <div id={cardId} className={base} data-testid={cardId}>
        <div className="relative h-[62%] overflow-hidden">
          <img src={img} alt={p.name} crossOrigin="anonymous" className="absolute inset-0 h-full w-full object-cover" />
          <img src={LOGO} alt="" crossOrigin="anonymous" className="absolute top-5 left-5 h-8 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]" />
        </div>
        <div className="h-[38%] border-t border-[#D4AF37]/30 p-7 flex flex-col" style={{ background: "#0C0A09" }}>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">{p.category === "jewellery" ? (insp ? `Inspired by ${insp}` : "Equine Evolve") : `Equine Evolve · ${p.category_name}`}</p>
          <h3 className="font-display text-[2rem] leading-tight text-white mt-1">{p.name}</h3>
          <p className="text-[#A39B95] text-sm mt-2">{p.category === "jewellery" ? "Bespoke gold · made to your measure" : "Handmade to order"}</p>
          <div className="mt-auto flex items-end justify-between">
            <p className="font-display text-3xl text-[#D4AF37]">From {eur(p.price)}</p>
            <span className="text-[9px] uppercase tracking-[0.2em] text-white/50">{SITE}</span>
          </div>
        </div>
      </div>
    );
  }

  if (vId === 2) {
    return (
      <div id={cardId} className={base} data-testid={cardId} style={{ background: "radial-gradient(circle at 50% 28%, #1A1614, #0C0A09 75%)" }}>
        <div className="absolute inset-4 border border-[#D4AF37]/40" />
        <div className="relative h-full flex flex-col items-center justify-center p-10 text-center">
          <img src={LOGO} alt="Equine Evolve" crossOrigin="anonymous" className="h-9 object-contain" />
          <div className="mt-8 w-[208px] h-[278px] overflow-hidden border border-[#D4AF37]/40">
            <img src={img} alt={p.name} crossOrigin="anonymous" className="h-full w-full object-cover" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mt-7">{p.category_name}</p>
          <h3 className="font-display text-3xl leading-tight text-white mt-2">{p.name}</h3>
          <p className="font-display text-2xl text-[#D4AF37] mt-3">From {eur(p.price)}</p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/45 mt-7">Handmade to order</p>
        </div>
      </div>
    );
  }

  if (vId === 3) {
    return (
      <div id={cardId} className={base} data-testid={cardId}>
        <img src={img} alt={p.name} crossOrigin="anonymous" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(12,10,9,0.6), rgba(12,10,9,0.78), rgba(12,10,9,0.92))" }} />
        <div className="absolute inset-0 flex flex-col p-8">
          <div className="flex justify-center">
            <img src={LOGO} alt="Equine Evolve" crossOrigin="anonymous" className="h-9 object-contain" />
          </div>
          <div className="my-auto text-center">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">{p.category === "jewellery" && insp ? `Inspired by ${insp}` : "The Equine Evolve ethos"}</p>
            <h3 className="font-display italic text-[3rem] leading-[1.02] text-white mt-4">{p.category === "jewellery" ? "“Wear a legend.”" : "“Live the legacy.”"}</h3>
            <p className="text-[#CFC7C1] text-sm mt-5 max-w-[270px] mx-auto leading-relaxed">{p.short_description}</p>
          </div>
          <div className="text-center">
            <p className="font-display text-2xl text-[#D4AF37]">From {eur(p.price)}</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/55 mt-2">{SITE}</p>
          </div>
        </div>
      </div>
    );
  }

  // vId === 4 — price feature
  return (
    <div id={cardId} className={base} data-testid={cardId} style={{ background: "radial-gradient(circle at 20% 80%, #1A1614, #0C0A09 70%)" }}>
      <div className="absolute top-0 right-0 w-[60%] h-full overflow-hidden">
        <img src={img} alt={p.name} crossOrigin="anonymous" className="h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #0C0A09, rgba(12,10,9,0.25) 55%, rgba(12,10,9,0) 100%)" }} />
      </div>
      <div className="relative h-full flex flex-col p-8">
        <img src={LOGO} alt="Equine Evolve" crossOrigin="anonymous" className="h-9 object-contain self-start" />
        <div className="mt-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">From</p>
          <p className="font-display text-[3.6rem] leading-none text-[#D4AF37]">{eur(p.price)}</p>
          <h3 className="font-display text-3xl text-white mt-4">{p.name}</h3>
          <p className="text-[#A39B95] text-sm mt-2 max-w-[210px]">{p.category === "jewellery" ? "Bespoke · made to your measure" : p.short_description}</p>
          <div className="mt-6 inline-block border border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 text-[11px] uppercase tracking-[0.25em]">Shop now</div>
        </div>
      </div>
    </div>
  );
}

export default function MarketingAssets() {
  const [products, setProducts] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(null);
  const allBusy = useRef(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      if (r.data?.length) setActiveSlug(r.data[0].slug);
    }).catch(() => {});
  }, []);

  const active = products.find((p) => p.slug === activeSlug);
  const activeIndex = products.findIndex((p) => p.slug === activeSlug);

  const exportCard = async (cardId, filename) => {
    const node = document.getElementById(cardId);
    if (!node) return;
    const dataUrl = await toPng(node, { pixelRatio: 3, cacheBust: true, backgroundColor: "#0C0A09" });
    const a = document.createElement("a");
    a.download = filename;
    a.href = dataUrl;
    a.click();
  };

  const handleDownload = async (p, v) => {
    const cardId = `asset-card-${p.slug}-${v.id}`;
    setBusy(cardId);
    try {
      await exportCard(cardId, `equine-evolve_${p.slug}_${v.name.toLowerCase().replace(/\s+/g, "-")}.png`);
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadAll = async (p) => {
    if (allBusy.current) return;
    allBusy.current = true;
    setDownloadingAll(true);
    try {
      for (const v of VARIANTS) {
        // eslint-disable-next-line no-await-in-loop
        await exportCard(`asset-card-${p.slug}-${v.id}`, `equine-evolve_${p.slug}_${v.name.toLowerCase().replace(/\s+/g, "-")}.png`);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 350));
      }
    } finally {
      allBusy.current = false;
      setDownloadingAll(false);
    }
  };

  const handleCopy = async (p, v) => {
    const id = `${p.slug}-${v.id}`;
    try {
      await navigator.clipboard.writeText(genCaption(p, v.id));
      setCopied(id);
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 1600);
    } catch (e) {
      /* clipboard blocked */
    }
  };

  return (
    <>
      <Seo title="Marketing Assets" description="Branded, share-ready Instagram story & feed cards for the Equine Evolve collection." />
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="min-h-screen bg-[#0C0A09]" data-testid="marketing-assets-page">
        {/* header */}
        <section className="relative border-b ee-hairline pt-32 pb-12 px-6 md:px-12 lg:px-20 max-w-[1500px] mx-auto">
          <div className="flex items-center gap-2 text-[#D4AF37] mb-4">
            <Instagram size={18} strokeWidth={1.5} />
            <p className="ee-overline">Social Media Kit</p>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">Marketing Assets</h1>
          <p className="text-[#A8A29E] mt-5 max-w-2xl leading-relaxed text-sm sm:text-base">
            Slick, on-brand Instagram story &amp; feed cards for every piece — five design variations each, with live pricing,
            ready-to-paste captions and a suggested posting schedule. Download a 1080×1920 PNG and post.
          </p>
        </section>

        {/* product tabs */}
        <div className="sticky top-0 z-30 bg-[#0C0A09]/90 backdrop-blur-md border-b ee-hairline">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-4 flex gap-2 overflow-x-auto no-scrollbar">
            {products.map((p) => (
              <button
                key={p.slug}
                data-testid={`asset-tab-${p.slug}`}
                onClick={() => setActiveSlug(p.slug)}
                className={`whitespace-nowrap px-5 py-2.5 text-sm tracking-wide transition-colors border ${
                  p.slug === activeSlug
                    ? "bg-[#D4AF37] text-[#0C0A09] border-[#D4AF37]"
                    : "text-[#A8A29E] border-white/10 hover:border-[#D4AF37]/50 hover:text-white"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>

        {/* gallery */}
        {active && (
          <section className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-14">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-12">
              <div>
                <p className="ee-overline mb-2">{active.category_name} · {eur(active.price)}</p>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-white">{active.name}</h2>
                <p className="text-[#A39B95] text-sm mt-2 max-w-xl">{active.short_description}</p>
              </div>
              <button
                data-testid={`download-all-${active.slug}`}
                onClick={() => handleDownloadAll(active)}
                disabled={downloadingAll}
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0C0A09] px-6 py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-[#B5952F] transition-colors disabled:opacity-60"
              >
                {downloadingAll ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                {downloadingAll ? "Preparing…" : "Download all 5"}
              </button>
            </div>

            <div className="flex flex-wrap gap-x-10 gap-y-14 justify-center sm:justify-start">
              {VARIANTS.map((v) => {
                const cardId = `asset-card-${active.slug}-${v.id}`;
                const copyId = `${active.slug}-${v.id}`;
                const isBusy = busy === cardId;
                const isCopied = copied === copyId;
                const date = scheduleFor(activeIndex * VARIANTS.length + v.id, v.format);
                return (
                  <div key={v.id} className="w-[360px]">
                    {/* card */}
                    <div className="border border-white/5 shadow-[0_24px_70px_rgba(0,0,0,0.55)]">
                      <AssetCard p={active} vId={v.id} cardId={cardId} />
                    </div>
                    {/* meta */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">{v.name}</span>
                        <span className="text-[10px] uppercase tracking-[0.15em] text-[#57534E] border border-white/10 px-2 py-1">{v.format} · 9:16</span>
                      </div>
                      <p className="flex items-center gap-2 text-xs text-[#A8A29E] mt-3">
                        <CalendarDays size={13} className="ee-gold-text" /> Suggested post: <span className="text-[#F5F5F4]">{date}</span>
                      </p>
                      <div className="flex gap-2 mt-4">
                        <button
                          data-testid={`download-${active.slug}-${v.id}`}
                          onClick={() => handleDownload(active, v)}
                          disabled={isBusy}
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A1614] border border-[#D4AF37]/40 text-[#D4AF37] px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-[#D4AF37] hover:text-[#0C0A09] transition-colors disabled:opacity-60"
                        >
                          {isBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                          {isBusy ? "Saving…" : "PNG"}
                        </button>
                        <button
                          data-testid={`copy-${active.slug}-${v.id}`}
                          onClick={() => handleCopy(active, v)}
                          className="flex-1 inline-flex items-center justify-center gap-2 border border-white/10 text-[#A8A29E] px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:border-[#D4AF37]/50 hover:text-white transition-colors"
                        >
                          {isCopied ? <Check size={14} className="text-[#D4AF37]" /> : <Copy size={14} />}
                          {isCopied ? "Copied" : "Caption"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
